import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  ChevronUp,
  Download,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { Skeleton } from '../../../components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table.js';
import { cn } from '../../../lib/utils.js';
import type { CustomFieldsItem, PlanningObjectItem, WorkfrontObjectItem } from '../hooks.js';
import type { ScenarioRow, ScenarioSortDirection, ScenarioSortField } from '../types.js';
import { ScenarioRow as ScenarioRowView } from './ScenarioRow.js';

interface ScenarioListTableProps {
  rows: ReadonlyArray<ScenarioRow>;
  isLoading: boolean;
  error: Error | null;
  pageSize?: number;
  sortField: ScenarioSortField;
  sortDirection: ScenarioSortDirection;
  onSortChange: (field: ScenarioSortField) => void;
  /** Total rows BEFORE pagination, used for the "Showing X-Y of N" footer. */
  totalUnfiltered: number;
  /** When true, render the expand-chevron column + sub-table panels. */
  showExpand: boolean;
  workfrontObjectsByScenario: Map<string, ReadonlyArray<WorkfrontObjectItem>>;
  customFieldsByScenario: Map<string, CustomFieldsItem>;
  planningObjectsByScenario: Map<string, ReadonlyArray<PlanningObjectItem>>;
  /** When true, render the trailing actions column. */
  showActions: boolean;
  /** When true (Developer only), the actions cell also shows the wrench-dropdown. */
  showActionsDropdown: boolean;
  /** Current free-text search term — threaded into rows so matches
   *  highlight in the scenario name. */
  searchQuery?: string;
  /** Called when the operator clicks the per-row search button inside
   *  an expanded row's Workfront-objects panel. Sets the page-level
   *  search input to the clicked object's ID. */
  onSearch?: (query: string) => void;
  /** Triggers the CSV download from the footer's Export dropdown.
   *  Same callback the toolbar's Export button uses — pass the
   *  same handler through to keep the two surfaces in sync. */
  onExportCsv?: () => void;
  /** Gate for the Export dropdown — false when there's nothing
   *  to export (empty filter result). */
  canExportCsv?: boolean;
}

export function ScenarioListTable({
  rows,
  isLoading,
  error,
  pageSize = 10,
  sortField,
  sortDirection,
  onSortChange,
  totalUnfiltered,
  showExpand,
  workfrontObjectsByScenario,
  customFieldsByScenario,
  planningObjectsByScenario,
  showActions,
  showActionsDropdown,
  searchQuery = '',
  onSearch,
  onExportCsv,
  canExportCsv = false,
}: ScenarioListTableProps): JSX.Element {
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  // Keyboard-navigation cursor — index within the current page. `null`
  // means "no row focused" (initial state); pressing ↓ on a fresh
  // page lands on row 0.
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [, setParams] = useSearchParams();

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const paged = rows.slice(start, start + pageSize);
  const colsBase = 6 + (showExpand ? 1 : 0) + (showActions ? 1 : 0);

  // Reset the keyboard cursor when the underlying row set or the
  // page index changes — landing on an out-of-bounds row would feel
  // jumpy.
  useEffect(() => {
    setActiveIndex(null);
  }, [safePage, rows.length]);

  // Window-level keydown handler for ↑ / ↓ / Enter row nav. Skipped
  // when the user is typing in a search box / textarea / contentEditable
  // surface; the command palette has its own listener for Cmd+K and
  // we don't want to fight it for ↑/↓ inside the palette either.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (isEditableTarget(e.target)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (paged.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((prev) => {
          const next = prev === null ? 0 : prev + 1;
          if (next < paged.length) return next;
          // Wrap to next page when there is one; otherwise stay put.
          if (safePage < totalPages) {
            setPage(safePage + 1);
            return 0;
          }
          return paged.length - 1;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((prev) => {
          if (prev === null) return paged.length - 1;
          if (prev > 0) return prev - 1;
          if (safePage > 1) {
            setPage(safePage - 1);
            return pageSize - 1;
          }
          return 0;
        });
      } else if (e.key === 'Enter' && activeIndex !== null) {
        const row = paged[activeIndex];
        if (!row) return;
        e.preventDefault();
        setParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('scenarioId', row.fusionId);
          return next;
        });
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [paged, activeIndex, safePage, totalPages, pageSize, setParams]);

  const toggle = (fusionId: string): void => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(fusionId)) next.delete(fusionId);
      else next.add(fusionId);
      return next;
    });
  };

  return (
    <div className="overflow-x-auto rounded-md border border-foreground/10 bg-black/10">
      <Table>
        <TableHeader className="sticky top-0 z-10 bg-[color:var(--surface-overlay)]/95 backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface-overlay)]/75">
          <TableRow className="border-foreground/10 hover:bg-transparent">
            {showExpand ? <TableHead className="w-10 text-foreground/50" /> : null}
            <TableHead className="w-10 text-center text-foreground/50">#</TableHead>
            <SortableHead
              field="name"
              label="Scenario"
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              className="min-w-[420px]"
            />
            <SortableHead
              field="applications"
              label="Application"
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
            <SortableHead
              field="description"
              label="Description"
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              className="min-w-[280px]"
            />
            <TableHead className="min-w-[220px] text-foreground/50">Run Metrics/Schedule</TableHead>
            <SortableHead
              field="modifiedAt"
              label="Modified"
              sortField={sortField}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              className="min-w-[180px]"
            />
            {showActions ? (
              // Sticky-right so the dropdown stays visible even when the
              // table overflows its horizontal container (common at <1440px
              // viewports — total min-w of the rest of the columns is
              // ~1380px). Background matches the page gradient-end so the
              // pinned column doesn't bleed scrolled-under content. z-20
              // outranks the sticky-top header's z-10 so the top-right
              // corner cell renders cleanly when both axes scroll.
              //
              // Width adapts to mode: business mode renders only the Comment
              // icon (one button, ~28px) → narrower column; admin + developer
              // add AI summary + dropdown → wider column. Matches the legacy
              // `wf-audit` DefaultTable layout (legacy's `showDevLink`
              // covered both admin + developer).
              <TableHead
                className={cn(
                  'sticky right-0 z-20 bg-[color:var(--surface-overlay)]/95 text-center text-foreground/50 shadow-[inset_1px_0_0_rgba(255,255,255,0.06)] backdrop-blur supports-[backdrop-filter]:bg-[color:var(--surface-overlay)]/75',
                  showActionsDropdown ? 'w-32' : 'w-20',
                )}
              >
                Actions
              </TableHead>
            ) : null}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <SkeletonRows showExpand={showExpand} showActions={showActions} />
          ) : error ? (
            <TableRow className="hover:bg-transparent">
              <TableCell
                colSpan={colsBase}
                className="p-6 text-center text-rose-700 dark:text-rose-300"
              >
                Could not load scenarios: {error.message}
              </TableCell>
            </TableRow>
          ) : paged.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={colsBase} className="p-12 text-center">
                <div className="flex flex-col items-center gap-2">
                  <span className="text-sm text-foreground/70">
                    No scenarios match the current filters.
                  </span>
                  {totalUnfiltered > 0 ? (
                    <span className="text-xs text-foreground/40">
                      {totalUnfiltered} scenario{totalUnfiltered === 1 ? '' : 's'} hidden — try
                      clearing search, status pills, or the filter drawer.
                    </span>
                  ) : null}
                </div>
              </TableCell>
            </TableRow>
          ) : (
            paged.map((row, i) => (
              <ScenarioRowView
                key={row.fusionId ?? `row-${i}`}
                row={row}
                index={start + i + 1}
                showExpand={showExpand}
                expanded={expanded.has(row.fusionId)}
                onToggleExpand={() => toggle(row.fusionId)}
                workfrontObjects={workfrontObjectsByScenario.get(row.fusionId) ?? []}
                customFields={customFieldsByScenario.get(row.fusionId)}
                planningObjects={planningObjectsByScenario.get(row.fusionId) ?? []}
                expandedColSpan={colsBase}
                showActions={showActions}
                showActionsDropdown={showActionsDropdown}
                searchQuery={searchQuery}
                onSearch={onSearch}
                keyboardActive={activeIndex === i}
              />
            ))
          )}
        </TableBody>
      </Table>
      <PaginationFooter
        page={safePage}
        totalPages={totalPages}
        startRow={paged.length > 0 ? start + 1 : 0}
        endRow={start + paged.length}
        filteredTotal={rows.length}
        unfilteredTotal={totalUnfiltered}
        onPageChange={setPage}
        onExportCsv={onExportCsv}
        canExportCsv={canExportCsv}
      />
    </div>
  );
}

function SortableHead({
  field,
  label,
  sortField,
  sortDirection,
  onSortChange,
  className,
}: {
  field: ScenarioSortField;
  label: string;
  sortField: ScenarioSortField;
  sortDirection: ScenarioSortDirection;
  onSortChange: (f: ScenarioSortField) => void;
  className?: string;
}): JSX.Element {
  const active = sortField === field;
  const Glyph = !active ? ChevronsUpDown : sortDirection === 'asc' ? ChevronUp : ChevronDown;
  return (
    <TableHead className={cn('text-foreground/60', className)}>
      <button
        type="button"
        onClick={() => onSortChange(field)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded px-1 py-0.5 transition-colors hover:text-foreground',
          active && 'text-foreground',
        )}
      >
        <span>{label}</span>
        <Glyph className="h-3.5 w-3.5" />
      </button>
    </TableHead>
  );
}

function SkeletonRows({
  showExpand,
  showActions,
}: {
  showExpand: boolean;
  showActions: boolean;
}): JSX.Element {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i} className="border-foreground/10 hover:bg-transparent">
          {showExpand ? <TableCell /> : null}
          <TableCell />
          <TableCell>
            <Skeleton className="h-4 w-48 bg-foreground/10" />
            <Skeleton className="mt-2 h-3 w-32 bg-foreground/10" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-7 w-32 bg-foreground/10" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-full bg-foreground/10" />
            <Skeleton className="mt-1 h-4 w-3/4 bg-foreground/10" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-32 bg-foreground/10" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-32 bg-foreground/10" />
          </TableCell>
          {showActions ? (
            // Mirror the real Actions cell's sticky-right so the skeleton
            // doesn't jump when the data lands.
            <TableCell className="sticky right-0 bg-[color:var(--surface-overlay)] shadow-[inset_1px_0_0_rgba(255,255,255,0.06)]">
              <Skeleton className="ml-auto h-6 w-20 bg-foreground/10" />
            </TableCell>
          ) : null}
        </TableRow>
      ))}
    </>
  );
}

function PaginationFooter({
  page,
  totalPages,
  startRow,
  endRow,
  filteredTotal,
  unfilteredTotal,
  onPageChange,
  onExportCsv,
  canExportCsv,
}: {
  page: number;
  totalPages: number;
  startRow: number;
  endRow: number;
  filteredTotal: number;
  unfilteredTotal: number;
  onPageChange: (p: number) => void;
  onExportCsv: (() => void) | undefined;
  canExportCsv: boolean;
}): JSX.Element {
  const filteredNote =
    filteredTotal !== unfilteredTotal ? ` (filtered from ${unfilteredTotal})` : '';
  // Legacy `wf-audit/.../DefaultTable.tsx` footer layout — three
  // columns, centred pagination cluster:
  //
  //   [ Showing X-Y of N scenarios ]   [ < Previous · 1 · 2 · Next > ]   [ Export ⌄ ]
  //
  // - Previous / Next carry ChevronLeft / ChevronRight glyphs so the
  //   navigation direction reads at a glance.
  // - Page numbers stay as outlined pills (yellow border on active).
  // - Ellipsis is a non-interactive `…` between distant boundary pages.
  // - Export dropdown anchors the right side; today it exposes a
  //   single "Export as CSV" action (matches the toolbar's button),
  //   but the dropdown shape leaves room for future formats without
  //   reflowing the row.
  return (
    <div className="grid grid-cols-3 items-center gap-3 border-t border-foreground/10 px-3 py-2 text-xs text-foreground/60">
      <span className="justify-self-start">
        Showing {startRow}-{endRow} of {filteredTotal} scenarios
        {filteredNote}
      </span>
      <div className="flex items-center gap-2 justify-self-center">
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-foreground/70 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-foreground/70"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Previous
        </button>
        {pageWindow(page, totalPages).map((p, i) =>
          p === '...' ? (
            <span key={`gap-${i}`} className="px-1 text-foreground/40">
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              className={cn(
                'inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-sm transition-colors',
                p === page
                  ? 'border border-[color:var(--brand-yellow)] bg-transparent text-foreground'
                  : 'border border-transparent text-foreground/70 hover:bg-foreground/10 hover:text-foreground',
              )}
              aria-current={p === page ? 'page' : undefined}
            >
              {p}
            </button>
          ),
        )}
        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          className="inline-flex items-center gap-1 rounded px-2 py-1 text-sm text-foreground/70 transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-foreground/70"
        >
          Next
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="justify-self-end">
        {onExportCsv ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                disabled={!canExportCsv}
                className="inline-flex items-center gap-1 rounded-md border border-foreground/15 px-2.5 py-1 text-sm text-foreground/80 transition-colors hover:border-foreground/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
              >
                Export
                <ChevronDown className="h-3.5 w-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[160px]">
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  onExportCsv();
                }}
                disabled={!canExportCsv}
              >
                <Download className="h-4 w-4" />
                <span>Export as CSV</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>
    </div>
  );
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (target.isContentEditable) return true;
  // Inside the command-palette popover (cmdk) we should also ignore
  // ↑/↓ — cmdk wires its own. Easiest signal: the trigger lives in
  // a Radix Dialog with `role="dialog"`.
  if (target.closest('[role="dialog"]')) return true;
  return false;
}

/**
 * Compact pagination window — `1 2 ... 6` shape. Keeps first + last,
 * the active page, and one neighbour on each side. Ellipses fill the
 * gaps.
 */
function pageWindow(current: number, total: number): Array<number | '...'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const result: Array<number | '...'> = [1];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  if (left > 2) result.push('...');
  for (let p = left; p <= right; p++) result.push(p);
  if (right < total - 1) result.push('...');
  result.push(total);
  return result;
}
